import React, { useCallback, useState, useEffect } from "react";
import styles from "../../Style/Components/file.module.scss";
import { useDropzone } from "react-dropzone";
import Icon from "../icons";
import { PrimaryButton } from "../Buttons";
import { APIService } from "../../Service";
import { toast } from "react-toastify";

export default function CustomFileUpload(props) {
  const {
    error,
    handleChange,
    onUploadComplete,
    uploadOnSelect = true,
    fileSize = 3, // default MB
    type,
    label,
    require,
    handleClear,
    file: externalFile,
  } = props;

  const [localFile, setLocalFile] = useState(null); // File object for preview
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [viewUrl, setViewUrl] = useState('');

  // keep localFile in sync if parent passes a File/Blob
  useEffect(() => {
    setLocalFile(
      externalFile instanceof File || externalFile instanceof Blob
        ? externalFile
        : null,
    );
  }, [externalFile]);

  // create and manage preview URL (revoke old ones)
  useEffect(() => {
    if (localFile instanceof File || localFile instanceof Blob) {
      const objUrl = URL.createObjectURL(localFile);
      setPreviewUrl(objUrl);
      return () => {
        URL.revokeObjectURL(objUrl);
        setPreviewUrl(""); // clear when unmount or file change
      };
    }

    if (typeof externalFile === "string" && externalFile) {
      setPreviewUrl(externalFile);
      return () => {
        setPreviewUrl("");
      };
    }

    setPreviewUrl("");
  }, [localFile, externalFile]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file) return null;
      setIsUploading(true);
      try {
        const is_post = true;
        const url = "general/upload";
        const data = {
          type: "ADMIN",
          file: file.name,
          is_public: true,
        };

        const response = await APIService(is_post, url, data);
        if (!response || response.status_code !== 200) {
          toast.error(response?.message || "Failed to get upload URL", {
            className: "e-toast",
          });
          setIsUploading(false);
          return null;
        }

        const aws_fields = response.data.upload_info.fields || {};
        const aws_url = response.data.upload_info.url;

        const formData = new FormData();
        Object.entries(aws_fields).forEach(([k, v]) => formData.append(k, v));
        formData.append("file", file, file.name);

        const s3Res = await fetch(aws_url, {
          method: "POST",
          body: formData,
        });

        if (!s3Res.ok) {
          toast.error("Upload to S3 failed", { className: "e-toast" });
          setIsUploading(false);
          return null;
        }

        const viewUrl = response.data.view_info;
        setIsUploading(false);

        if (typeof onUploadComplete === "function") {
          onUploadComplete(viewUrl);
          setViewUrl(viewUrl);
        }

        return viewUrl;
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while uploading the file.", {
          className: "e-toast",
        });
        setIsUploading(false);
        return null;
      }
    },
    [onUploadComplete],
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (typeof handleChange === "function") handleChange(file);
        setLocalFile(file);
        if (uploadOnSelect) {
          await uploadFile(file);
        }
      }
    },
    [handleChange, uploadOnSelect, uploadFile],
  );

  const onDropRejected = useCallback(() => {
    toast.error(`Image must be below ${fileSize}MB`, { className: "e-toast" });
  }, [fileSize]);

  // NOTE: fixed extensions (leading dots). You can also use { "image/*": [] } if you don't want to validate extensions.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    maxSize: fileSize * 1024 * 1024,
    maxFiles: 1,
    multiple: false,
    accept: {
      "image/png": [".png"],
      // "image/jpeg": [".jpeg", ".jpg"],
      // "image/webp": [".webp"],
    },
  });

  return (
    <div>
      {externalFile || localFile ? (
        <div
          className={` ${
            props.error && viewUrl.trim() === ""
              ? styles.e_uploaded_wrapper_error
              : styles.e_uploaded_wrapper
          }`}
        >
          <div className="row">
            <div className="col-4">
              <div className="d-flex bg-white bg-opacity-43 p-1 position-relative">
                {previewUrl && (
                  <img
                    width={120}
                    height={120}
                    src={previewUrl}
                    alt="preview"
                    className="img-fluid object-fit-cover"
                  />
                )}

                <div className=" position-absolute end-0 top-0">
                  <PrimaryButton
                    icon="close"
                    ClassName="bg-alizarin-red p-1 d-flex justify-content-center align-items-center w-25x h-25x rounded-circle text-white"
                    size="12"
                    onPress={handleClear}
                  />
                </div>
                {isUploading && (
                  <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-black bg-opacity-50">
                    <div className="spinner-border text-white" role="status">
                      <span className="visually-hidden">Uploading...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* preserved commented UI blocks */}
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-4">
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {type === 2 ? (
                <div
                  className={`${
                    props.error && viewUrl.trim()=== "" ? styles.e_uploaded_wrapper_error : ""
                  } d-flex justify-content-center rounded-8 py-25 bg-white bg-opacity-43 e-cursor-pointer`}
                >
                  <label className="text-black   fw-400  fs-14 pb-2">
                    {label}
                    {require && <span className="text-danger">*</span>}
                  </label>
                  {isDragActive ? (
                    <h5 className="m-0 fs-16 fw-400 text-purple  text-center">
                      Drop Your File Here
                    </h5>
                  ) : (
                    <div className="d-flex w-100 h-100 flex-column align-items-center justify-content-center row-gap-2">
                      <Icon icon="upload" size="34px" />
                      <p className="m-0 fs-10 lh-12 fw-400 text-davys-gray text-center">
                        Drag & Drop <br /> or <br />
                        <span className=" text-oxford-blue"> Upload</span> files
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  className={
                    props.error && viewUrl.trim() === ""
                      ? styles.e_uploaded_wrapper_error
                      : styles.e_uploaded_wrapper
                  }
                >
                  <label className="text-black   fw-400  fs-14 pb-2">
                    {label}
                    {require && <span className="text-danger">*</span>}
                  </label>
                  {isDragActive ? (
                    <h5 className="m-0 fs-16 fw-400 text-purple ">
                      Drop Your File Here
                    </h5>
                  ) : (
                    <div className="d-flex w-100 h-100 flex-column align-items-center justify-content-center row-gap-2">
                      <Icon icon="upload" size="34px" />
                      <p className="m-0 fs-14 fw-400 text-purple ">
                        Choose file{" "}
                        <span className=" text-gray">or drop here</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
              <div className="d-flex flex-column align-items-start pt-2 ps-2">
                <p className="mb-0 text-davys-gray fw-400 fs-10 lh-16 ">
                  Files supported: PNG
                </p>
                <p className="mb-0 text-davys-gray fw-400 fs-10 lh-16 ">
                  Maximum size: {fileSize} MB
                </p>
              </div>
            </div>
          </div>
          <div className="col-5">
            <div className="d-flex justify-content-center flex-column  h-100">
              <p className="text-davys-gray fw-400 fs-10 lh-16 mb-0">
                Recommended size: 512 × 512 px
              </p>
              {error && viewUrl.trim() === "" && (
                <p className="mb-0 fs-10 fw-400 text-danger">{error}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
